import { useContext, useEffect, useState, useCallback } from "react";
import Footer from "../../components/Footer";
import Loader from "../../components/Loader/Loader";
import GlobalConfig from "../../context/GlobalConfig";
import { useParams } from "react-router-dom";
import { languageData } from "../../constants/languageData";
import JourneyConfig from "../../context/JourneyConfig";
import { convertImageToUrl } from "../../utils/base64ToUrlUtility";
import { getDocumentFileTypes } from "../../constants/fileTypeConstants";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import PdfCanvasPreview from "../../components/PdfCanvasPreview";
import { getDocumentCountry, getDocumentIdType } from "../../utils/documentFields";

/**
 * Route to the generic "Oops! Something went wrong" screen. Used when
 * update-journey itself fails; upload failures fall back to a base64
 * payload instead of landing here.
 */
function routeToErrorScreen(stepperFunc, context = "upload") {
	console.error(`[DocumentCheck] Routing to error screen | context=${context}`);
	sessionStorage.setItem("error", "true");
	sessionStorage.removeItem("errorCategory");
	sessionStorage.removeItem("errorReason");
	const currentStepFromStorage = sessionStorage.getItem("currentStep");
	stepperFunc(currentStepFromStorage ? parseInt(currentStepFromStorage) : 0);
}

// A reusable document check component based on ImageCheck.jsx
// Adds controllable callbacks for retake/confirm to support phased flows
const DocumentCheck = ({
	image,
	stepperFunc,
	isFirstDocumentVerificationDone,
	currentDocumentStep,
	currentStep,
	capturingMode,
	preventBack,
	autoCaptureLoadingTime,
	currentDocumentRetries,
	// New props to integrate with phased capture flow
	phase, // 'front-check' | 'back-check'
	onRetake, // callback when user clicks button1 (Retake)
	onConfirm // callback after successful save (button2)
}) => {
	const { config } = useContext(GlobalConfig);
	const { journey, updateJourney, getJourney } = useContext(JourneyConfig);
	const documentFailureReason = sessionStorage.getItem("documentFailureReason");
	const [sameSideDocumentFlagNumber] = useState(documentFailureReason ? (documentFailureReason === "ONLY_FRONT" ? 1 : documentFailureReason === "ONLY_BACK" ? 2 : -1) : -1);
	const [isLoading, setIsLoading] = useState(false);
	const language = sessionStorage.getItem("language") ?? "en";
	const { journeyId } = useParams();
	const [imageUrl, setImageUrl] = useState(null);
	const color = config?.branding?.accentColor;
	const isPdf =
		typeof imageUrl === "string" &&
		(imageUrl.startsWith("data:application/pdf") || imageUrl.startsWith("data:application/x-pdf"));

	// Normalize phase -> triggeredFrom for saving
	const triggeredFrom = phase === "front-check" ? "front" : "back";

	useEffect(() => {
		if (!image) return;
		if (typeof image !== "string") {
			console.error("[DocumentCheck] Invalid image - not a base64 string");
			return;
		}
		setImageUrl(image);
	}, [image]);

	useEffect(() => {
		if (!image) {
			// If no image supplied, go back a step like ImageCheck
			if (typeof currentStep !== "undefined" && stepperFunc) {
				stepperFunc(parseInt(currentStep) - 1);
			}
		}
	}, []);

	// This document's own country and type, not the first document's (IDX-677). Mirrors the
	// capture screens so the value persisted at DOC_FRONT matches what was shown and captured.
	const [currentPageState] = useState({
		country: getDocumentCountry(journey, currentDocumentStep),
		idType: getDocumentIdType(journey, currentDocumentStep),
	});

	const saveDataAndMove = useCallback(async (e) => {
		if (e?.preventDefault) e.preventDefault();
		try {
			preventBack(true);
			setIsLoading(true);
			const { front: frontFileType, back: backFileType } = getDocumentFileTypes(currentDocumentStep);
			const fileType = triggeredFrom === "back" ? backFileType : frontFileType;

			// 3 internal retries with a fresh presigned URL each attempt
			// happen inside convertImageToUrl. A non-null error here is
			// terminal.
			const { s3Key, error } = await convertImageToUrl(image, journeyId, fileType);

			// Rate-limit cap hit → journey is already TERMINATED on the
			// backend. Ping update-journey so Settings.jsx switches to
			// the VerificationTerminated screen.
			if (error && error.terminateJourney) {
				try {
					await updateJourney(triggeredFrom === "back" ? "DOC_BACK" : "DOC_FRONT", {}, currentStep);
				} catch (signalErr) {
					console.error("[DocumentCheck] Failed to surface terminated state", signalErr);
				}
				return;
			}

			// Any other upload error → fall back to embedding the raw base64
			// in update-journey (s3Key is "" here, so the frontDocBase64 /
			// backDocBase64 spreads below engage) instead of dead-ending on
			// the error screen.
			if (error) {
				console.warn(`[DocumentCheck] Upload failed (${error.code}) — falling back to base64 payload`);
			}

			const persistImageUrl = s3Key;

			if (triggeredFrom === "back") {
				await updateJourney(
					"DOC_BACK",
					{
						backDoc: persistImageUrl,
						captureMode: capturingMode,
						autoCaptureLoadingTime: autoCaptureLoadingTime,
						currentDocumentStep: currentDocumentStep + 1,
						currentDocumentRetries: currentDocumentRetries,
						...(persistImageUrl === "" && { backDocBase64: image })
					},
					currentStep
				);
			} else if (triggeredFrom === "front") {
				if (currentPageState?.idType && currentPageState?.country) {
					sessionStorage.setItem("selectedCountry", currentPageState?.country);
				}
				await updateJourney(
					"DOC_FRONT",
					currentPageState?.idType && currentPageState?.country
						? {
								frontDoc: persistImageUrl,
								captureMode: capturingMode,
								autoCaptureLoadingTime: autoCaptureLoadingTime,
								currentDocumentStep: currentDocumentStep + 1,
								currentDocumentRetries: currentDocumentRetries,
								sameSideDocumentFlagNumber: sameSideDocumentFlagNumber,
								...(persistImageUrl === "" && { frontDocBase64: image }),
								...currentPageState
							}
						: {
								frontDoc: persistImageUrl,
								captureMode: capturingMode,
								autoCaptureLoadingTime: autoCaptureLoadingTime,
								currentDocumentStep: currentDocumentStep + 1,
								currentDocumentRetries: currentDocumentRetries,
								sameSideDocumentFlagNumber: sameSideDocumentFlagNumber,
								...(persistImageUrl === "" && { frontDocBase64: image }),
							},
					currentStep
				);
			}

			onConfirm();
		} catch (error) {
			console.error("[DocumentCheck] Error during save:", error);
			setIsLoading(false);
			preventBack(false);
			routeToErrorScreen(stepperFunc, "updateJourney");
			return;
		} finally {
			setIsLoading(false);
			preventBack(false);
		}
	}, [
		preventBack,
		currentDocumentStep,
		triggeredFrom,
		image,
		journeyId,
		capturingMode,
		autoCaptureLoadingTime,
		currentDocumentRetries,
		currentPageState,
		sameSideDocumentFlagNumber,
		journey,
		currentStep,
		stepperFunc,
		updateJourney,
		onConfirm
	]);

	useEffect(() => {
		if (journeyId && !journey) getJourney(journeyId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [journeyId]);

	if (isLoading) return <Loader />;
	return (
		<>
			<div className="parent-container">
				<div className="main">
					<div className="left-section">
						{imageUrl && !isPdf && (
							<img
								src={imageUrl}
								alt="preview"
								onError={(e) => {
									console.error("[DocumentCheck] Image failed to load", e);
								}}
							/>
						)}
						{imageUrl && isPdf && (
							<div className="flex flex-col w-full h-full min-h-[400px]" style={{ gap: 8, overflow: "visible" }}>
								{/* Use react-pdf for all devices with base64 data */}
								<PdfCanvasPreview
									file={imageUrl}
									openPdfLabel={languageData[language]?.pdfOpenLabel ?? "Open PDF"}
									previewHint={languageData[language]?.pdfPreviewHint ?? "If preview doesn't appear, you can still continue."}
									onError={(e) => console.error("[DocumentCheck] react-pdf preview failed:", e)}
								/>
							</div>
						)}
					</div>
					<div
						className="right-section md:items-start items-center"
						style={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "space-between",
							gap: 24,
							fontFamily: "Inter, sans-serif"
						}}
					>
						<div className="flex flex-col">
							<div className="text-[18px] md:text-[28px] font-semibold text-[#000000] text-center md:text-left">
								{languageData[language]?.imageCheck?.[phase === "front-check" ? "frontHeading" : "backHeading"]}
							</div>
							<div className="mt-4 md:mt-3 text-[12px] md:text-[16px] font-semibold text-[#656565] text-center md:text-left md:mb-0  mb-3">{languageData[language]?.imageCheck?.subHeading}</div>
						</div>
						<div className="flex flex-col justify-center items-center font-sans w-full">
							<div className="flex md:flex-col md:gap-[0px] gap-4 justify-center w-full">
								<div className="md:mb-4 mb-2 text-[16px] md:mt-6 md:mt-0 w-full">
									<button
										className=" py-3 px-4"
										onClick={onRetake}
										style={{
											color,
											textDecoration: "none",
											background: "none",
											border: `1px solid ${color}`,
											borderRadius: 4,
											cursor: "pointer",
											fontSize: "inherit",
											fontWeight: "inherit",
											textAlign: "inherit",
											display: "flex",
											width: "100%",
											alignItems: "center",
											justifyContent: "center"
										}}
									>
										{languageData[language]?.imageCheck?.button1}
									</button>
								</div>
								<button
									className="flex flex-row items-center justify-center gap-4 w-full text-white py-3 px-4 rounded text-[16px]"
									style={{ backgroundColor: color, height: "fit-content" }}
									onClick={(e) => saveDataAndMove(e)}
								>
									{languageData[language]?.imageCheck?.button2}
								</button>
							</div>
							<Footer style={{ marginTop: "1rem" }} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default DocumentCheck;
